/**
 * ONE TAP — trails.
 *
 * Everything in the game that draws a *line through time*:
 *
 *  - the thrown gun's arc, team-coloured when empty and HOT AMBER when chambered. This
 *    is the single most important trail in the game: DESIGN.md 4 requires that anyone,
 *    anywhere in the arena, can answer "is that pass loaded?" in one glance.
 *  - dash wakes and wall-run scrapes
 *  - the wounded player's bleed trail (enemy intel — DESIGN.md 1)
 *  - the grapple tether, which is a genuinely swept rope mesh with catenary sag, a taut
 *    vibration and a tension pulse travelling up it — never a debug line.
 *
 * Ribbons are pooled into two big dynamic buffers (one additive, one matte ink) so the
 * whole system is two draw calls no matter how many trails are live. Ribbon width is
 * expanded in the vertex shader against the view vector, so a trail always faces the
 * camera without any CPU billboarding and without ever collapsing to zero width.
 *
 * EVERY LOAD-BEARING TRAIL HERE IS MATTE. The arena is white and the sky is blue, and an
 * additive ribbon over either has nothing to add — it is the background plus epsilon. So
 * the arc that carries the information is an opaque saturated stroke inside a drawn ink
 * edge, and the additive pool is reduced to what it is actually good at: the blooming
 * halo that says "hot" once the matte stroke has already said "there".
 *
 * Nothing here allocates during a match: pools, point ring buffers and typed arrays are
 * all built once in the constructor.
 *
 * Client-only: touches `three`. Never import from src/shared.
 */

import * as THREE from 'three';
import {
  GRAPPLE_NONE,
  PLAYER_EYE_HEIGHT,
  PLAYER_HEIGHT,
  TEAM_COLORS,
  GRAPPLE_MIN_LENGTH,
} from '@shared/constants';
import { clamp01, dirFromYawPitch, rightFromYaw, v3, type Vec3 } from '@shared/math';
import { GunState, HealthState, MoveState, type GunEntity, type PlayerState, type WorldState } from '@shared/types';
import { applyVfxBlend, P_CHIP, P_INK, P_RING, P_SPARK, VFX, type ParticleSystem } from './particles';

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

/** Ribbons per pool and points per ribbon. 48 x 40 covers a 3v3 scramble twice over. */
const RIBBON_CAPACITY = 48;
const RIBBON_POINTS = 40;

/**
 * Fraction of a matte ribbon's half-width given over to its ink edge. At 0.34 a trail
 * reads as a coloured stroke inside a drawn outline at any size.
 */
const RIBBON_INK_EDGE = 0.34;

/**
 * MINIMUM ANGULAR HALF-WIDTH, radians — per ribbon, not per pool.
 *
 * Distance may thin a trail but must never erase it, so every ribbon carries a floor on
 * how narrow it is allowed to get on screen. At the base 92 degree FOV one 1080p pixel
 * is about 0.00192 rad, so these numbers are very nearly "pixels across, halved".
 *
 * They are deliberately NOT all the same value, because a shared floor is what lets a
 * background trail catch up with a foreground one at range: every ribbon in the arena
 * eventually converges on the floor, and at that point the only thing separating a
 * chambered pass from a wall-run scuff is hue. So the ordering here IS the information
 * hierarchy — the loaded pass is the widest line in the game at any distance, and
 * everything that is merely atmosphere is guaranteed to stay thinner than it.
 *
 * The amber halo's floor is deliberately UNDER the loaded stroke's. The halo is the one
 * additive part of the arc; if distance let it grow to match the matte stroke it would
 * sit exactly on top of it and wash the drawn amber line out to white — which on white
 * architecture is the same as deleting it.
 */
const MIN_ANG_LOADED = 0.0034;
const MIN_ANG_EMPTY = 0.0020;
const MIN_ANG_HALO = 0.0016;
const MIN_ANG_BLEED = 0.0018;
const MIN_ANG_DASH = 0.0014;
const MIN_ANG_SCRAPE = 0.0012;

/** A new point is only appended once the source has moved this far, in metres. */
const GUN_TRAIL_MIN_DIST = 0.14;

/**
 * THE PASS. The most important effect in the game, and the one thing every player must
 * be able to read across the whole arena, against white plaster AND against blue sky.
 *
 * The two states are separated on four axes at once, so no single failure — glare, a
 * matching background, a colour-blind viewer — can collapse them into each other:
 *
 *            EMPTY                              CHAMBERED
 *   colour   team, matte, INK spine             AMBER, matte, solid
 *   weight   thin                               2.4x wider, and wider still at range
 *   length   short, dies quickly                long, hangs in the air
 *   RHYTHM   a continuous scratch               a BEADED line: evenly spaced pips
 *   extras   none                               blooming halo + ignition embers
 *
 * The rhythm row is the one that does not depend on the viewer's colour vision, on the
 * exposure of the background, or on the trail being wide enough on screen to have a
 * discernible hue at all. A chambered pass is a dotted line and an empty one is a solid
 * one, and that difference survives greyscale, glare, distance and a colour-blind player
 * — which no hue-only difference does. The other four rows are still all there; this is
 * the one that holds when they fail.
 *
 * The empty arc is deliberately a quiet scratch. Half of "that pass is loaded" being
 * legible is the empty pass refusing to compete with it.
 *
 * Note what the empty arc is NOT: it is not pale. A team-blue stroke with a light grey
 * highlight down the middle is two low-contrast values against white plaster and against
 * a blue sky both, so the empty pass used to be the one trail in the game that could
 * genuinely disappear. It now carries a DARK spine instead — value contrast survives any
 * background — while staying thin, short-lived and translucent so it never competes with
 * amber for attention.
 */
const GUN_TRAIL_AGE_EMPTY = 0.34;
const GUN_TRAIL_AGE_LOADED = 0.75;
const GUN_TRAIL_WIDTH_EMPTY = 0.11;
const GUN_TRAIL_WIDTH_LOADED = 0.26;
const GUN_TRAIL_ALPHA_EMPTY = 0.7;
const GUN_HALO_WIDTH = 0.78;
const GUN_HALO_AGE = 0.42;
const GUN_HALO_ALPHA = 0.5;
/**
 * Ignition sparks shed by a chambered gun in flight, per second.
 *
 * Deliberately no higher than the ember rate. Sparks are additive, and additive over a
 * sunlit white atrium or an open blue sky is very nearly nothing — spending two thirds of
 * the particle budget on them, as this used to, bought a faint warm haze and a lot of
 * fill rate. The budget now goes to the matte half, which is the half that reads.
 */
const LOADED_SPARK_HZ = 26;
/** Matte ember chips shed by a chambered gun, per second. These are what read on white. */
const LOADED_EMBER_HZ = 26;

/**
 * THE BEAT OF A LOADED PASS.
 *
 * One hollow matte amber pip dropped every 1/CHAMBER_PIP_HZ seconds, left standing in the
 * air where the gun was. A throw crosses the arena in well under a second, so this lays
 * down roughly eight to twelve evenly spaced marks along the arc — a dotted line, drawn
 * in the one colour that means "chambered", readable as a *pattern* long before its hue
 * is resolvable.
 *
 * The pips are hollow rings, which matters twice over: they cannot hide an enemy standing
 * behind the pass, and they are unmistakably a different graphic from the solid embers
 * that fall away from the arc. And they are pinned in the air rather than carried along
 * with the gun, so the eye reads the whole flight path at once instead of a comet head.
 */
const CHAMBER_PIP_HZ = 13;
const CHAMBER_PIP_SIZE0 = 0.10;
const CHAMBER_PIP_SIZE1 = 0.66;
const CHAMBER_PIP_LIFE = 0.52;

const DASH_TRAIL_AGE = 0.30;
const DASH_TRAIL_WIDTH = 0.95;
/**
 * A dash wake is atmosphere, not information. Kept translucent so it tints the frame
 * without ever becoming a body-sized shape an enemy could hide behind.
 */
const DASH_TRAIL_ALPHA = 0.42;

const SCRAPE_AGE = 0.42;
const SCRAPE_WIDTH = 0.20;
/** Grit thrown off a wall-run, per second. Matte: a scuff, not a firework. */
const SCRAPE_GRIT_HZ = 26;

const BLEED_AGE = 2.6;
const BLEED_WIDTH = 0.095;
const BLEED_DRIP_HZ = 7;

/**
 * THE TETHER.
 *
 * A playtester could not find the grapple at all, and one of the reasons is that when it
 * did fire there was almost nothing on screen to see: a 4 cm tube stretched across sixty
 * metres of atrium is a hairline at the hand and literally sub-pixel at the anchor. A
 * traversal verb whose feedback disappears at the distance it operates over is a verb
 * nobody discovers.
 *
 * So the rope is now a ROPE. Two numbers do that work:
 *
 *   ROPE_RADIUS       its honest world thickness, close up. Big enough to read as a
 *                     physical line coming out of your hand rather than a debug ray.
 *   ROPE_MIN_ANGULAR  a floor on its half-width in RADIANS, applied per ring against the
 *                     distance to the eye — exactly the guarantee the ribbon pool gives
 *                     every trail (see MIN_ANG_*). Distance may thin the tether, but it
 *                     may never erase it, so the far end at the anchor stays a drawn line
 *                     the whole way out. At the base 92 degree FOV one 1080p pixel is
 *                     about 0.00192 rad, so 0.0026 is a ~2.7px floor on the half-width.
 *
 * The floor is deliberately UNDER the loaded pass's (0.0034): the tether is traversal, and
 * traversal must never out-shout the one thing every player has to read across the arena.
 */
const ROPE_POOL = 6;
const ROPE_RINGS = 26;
const ROPE_SIDES = 6;
const ROPE_RADIUS = 0.06;
const ROPE_MIN_ANGULAR = 0.0026;
/** Fraction of the rope's length over which each end tapers into its attachment. */
const ROPE_TAPER_SPAN = 0.08;
const ROPE_TAPER_MIN = 0.45;
/** Maximum sag, metres, at full slack. */
const ROPE_MAX_SAG = 2.1;
const ROPE_VIBRATION = 0.045;

// ---------------------------------------------------------------------------
// Ribbon GLSL
// ---------------------------------------------------------------------------

const RIBBON_VERT = /* glsl */ `
attribute vec3 aTan;
attribute vec3 aCol;
attribute vec3 aCore;
attribute float aSide;
attribute vec3 aW;   // width, alpha, minimum angular half-width

varying vec3 vCol;
varying vec3 vCore;
varying float vAcross;
varying float vAlpha;

void main() {

	vec4 mv = modelViewMatrix * vec4( position, 1.0 );

	vec3 t = ( modelViewMatrix * vec4( aTan, 0.0 ) ).xyz;
	float tl = length( t );
	t = tl > 1e-5 ? t / tl : vec3( 0.0, 1.0, 0.0 );

	// Expand across the view vector so the ribbon always faces the camera. When the
	// trail points straight at the eye the cross product degenerates, so fall back to a
	// stable screen axis instead of letting the ribbon vanish.
	vec3 vd = normalize( - mv.xyz );
	vec3 s = cross( t, vd );
	float sl = length( s );
	s = sl > 1e-4 ? s / sl : normalize( cross( t, vec3( 0.0, 0.0, 1.0 ) ) + vec3( 1e-3, 0.0, 0.0 ) );

	// A pass thrown across the arena must still be a LINE when it gets there. Floor the
	// ribbon at a fixed angular width so distance can thin it but never erase it — the
	// same guarantee the tracer gets, and the reason a loaded arc stays legible from the
	// far side of the map (DESIGN.md 4).
	//
	// The floor is per-ribbon, so the loaded pass keeps its weight advantage over every
	// other trail at every distance instead of all of them converging on one width.
	float dist = length( mv.xyz );
	float wide = max( aW.x, dist * aW.z * 2.0 );

	mv.xyz += s * ( aSide * wide * 0.5 );

	vCol = aCol;
	vCore = aCore;
	vAcross = aSide;
	vAlpha = aW.y;

	gl_Position = projectionMatrix * mv;

}
`;

const RIBBON_FRAG = /* glsl */ `
uniform float uBloom;
uniform vec3 uInk;
uniform float uEdge;

varying vec3 vCol;
varying vec3 vCore;
varying float vAcross;
varying float vAlpha;

void main() {

	float across = abs( vAcross );
	float w = fwidth( across ) + 0.02;

	// Hard-edged band with a hot inner stroke: reads as a drawn line with a highlight,
	// which is what makes a loaded pass legible from the far side of the arena.
	float body = 1.0 - smoothstep( 1.0 - w, 1.0, across );
	// The hot stroke is deliberately narrow. If it were wide the whole ribbon would read
	// as white and a loaded pass would lose its hot-yellow identity at range.
	float core = 1.0 - smoothstep( 0.08, 0.26, across );

	vec3 col = mix( vCol, vCore, core );
	float a = body * vAlpha;
	if ( a <= 0.002 ) discard;

	#ifdef VFX_ADDITIVE

		gl_FragColor = vec4( col * a, uBloom * a );

	#else

		// Matte ribbons carry the same inverted-hull ink edge as the world geometry.
		// This is the entire reason a team-blue arc still reads when it crosses a blue
		// sky: the colour may match the background, the drawn line never does.
		float inner = 1.0 - smoothstep( 1.0 - uEdge - w, 1.0 - uEdge, across );
		col = mix( uInk, col, inner );
		gl_FragColor = vec4( col * a, a );

	#endif

}
`;

// ---------------------------------------------------------------------------
// Ribbon pool
// ---------------------------------------------------------------------------

export interface RibbonStyle {
  /** Outer band colour (sRGB hex). */
  body: number;
  /**
   * Inner stroke colour (sRGB hex). On a MATTE ribbon this must never be `VFX.core`:
   * white down the middle of a stroke drawn on white architecture is a hole in the
   * shape. Incandescent white belongs to the additive pool only.
   */
  core: number;
  /** Width at the head, metres. */
  width: number;
  /** Seconds a point survives. */
  maxAge: number;
  /** Metres of travel before a new point is appended. */
  minDist: number;
  /** Fraction of the head width the tail tapers to. */
  tailScale: number;
  /** Master opacity. */
  alpha: number;
  /** Screen-space floor on the half-width, radians. See MIN_ANG_* above. */
  minAngular: number;
}

const _style: RibbonStyle = {
  body: VFX.ink,
  core: VFX.ink,
  width: 0.1,
  maxAge: 0.5,
  minDist: 0.1,
  tailScale: 0.15,
  alpha: 1,
  minAngular: MIN_ANG_SCRAPE,
};

const _c = new THREE.Color();

/**
 * A fixed set of ribbons packed into one geometry. Unused points collapse onto their
 * neighbour, which produces zero-area triangles the rasteriser throws away — so the
 * whole pool is one draw call whether one trail is live or forty.
 */
class RibbonPool {
  readonly mesh: THREE.Mesh;

  private readonly capacity: number;
  private readonly points: number;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;

  private readonly pos: Float32Array;
  private readonly tan: Float32Array;
  private readonly col: Float32Array;
  private readonly core: Float32Array;
  private readonly side: Float32Array;
  private readonly wid: Float32Array;
  private readonly attrs: THREE.BufferAttribute[];

  /** Per-ribbon ring buffer of world points and their ages. */
  private readonly ptPos: Float32Array;
  private readonly ptAge: Float32Array;

  private readonly used: Uint8Array;
  private readonly fading: Uint8Array;
  private readonly start: Int32Array;
  private readonly count: Int32Array;
  private readonly dirty: Uint8Array;
  private readonly collapsed: Uint8Array;
  private readonly last: Float32Array;
  private readonly styleWidth: Float32Array;
  private readonly styleAge: Float32Array;
  private readonly styleMinDist: Float32Array;
  private readonly styleTail: Float32Array;
  private readonly styleAlpha: Float32Array;
  private readonly styleMinAngular: Float32Array;
  private readonly styleBody: Float32Array;
  private readonly styleCore: Float32Array;

  private dirtyLo = Number.MAX_SAFE_INTEGER;
  private dirtyHi = -1;

  constructor(capacity: number, points: number, additive: boolean, renderOrder: number) {
    this.capacity = capacity;
    this.points = points;

    const verts = capacity * points * 2;
    this.pos = new Float32Array(verts * 3);
    this.tan = new Float32Array(verts * 3);
    this.col = new Float32Array(verts * 3);
    this.core = new Float32Array(verts * 3);
    this.side = new Float32Array(verts);
    this.wid = new Float32Array(verts * 3);

    // Side alternates per vertex pair and never changes again.
    for (let i = 0; i < verts; i += 2) {
      this.side[i] = -1;
      this.side[i + 1] = 1;
    }

    const quads = capacity * (points - 1);
    const index = verts > 65535 ? new Uint32Array(quads * 6) : new Uint16Array(quads * 6);
    let w = 0;
    for (let r = 0; r < capacity; r++) {
      const base = r * points * 2;
      for (let s = 0; s < points - 1; s++) {
        const a = base + s * 2;
        index[w++] = a;
        index[w++] = a + 1;
        index[w++] = a + 3;
        index[w++] = a;
        index[w++] = a + 3;
        index[w++] = a + 2;
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.name = additive ? 'vfx-ribbons-add' : 'vfx-ribbons-ink';
    const mk = (data: Float32Array, size: number): THREE.BufferAttribute => {
      const a = new THREE.BufferAttribute(data, size);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };
    const aPos = mk(this.pos, 3);
    const aTan = mk(this.tan, 3);
    const aCol = mk(this.col, 3);
    const aCore = mk(this.core, 3);
    const aSide = mk(this.side, 1);
    const aW = mk(this.wid, 3);
    geo.setAttribute('position', aPos);
    geo.setAttribute('aTan', aTan);
    geo.setAttribute('aCol', aCol);
    geo.setAttribute('aCore', aCore);
    geo.setAttribute('aSide', aSide);
    geo.setAttribute('aW', aW);
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    // Side is static; the rest are rewritten every frame for live ribbons only.
    this.attrs = [aPos, aTan, aCol, aCore, aW];

    const defines: Record<string, string> = {};
    if (additive) defines.VFX_ADDITIVE = '';

    const mat = new THREE.ShaderMaterial({
      name: geo.name,
      defines,
      uniforms: {
        uBloom: { value: additive ? 0.85 : 0 },
        uInk: { value: new THREE.Color().setHex(VFX.ink, THREE.SRGBColorSpace) },
        uEdge: { value: RIBBON_INK_EDGE },
      },
      vertexShader: RIBBON_VERT,
      fragmentShader: RIBBON_FRAG,
      side: THREE.DoubleSide,
      depthTest: true,
      toneMapped: false,
    });
    applyVfxBlend(mat, additive ? 'add' : 'over');

    this.geometry = geo;
    this.material = mat;
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.name = geo.name;
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = renderOrder;

    this.ptPos = new Float32Array(capacity * points * 3);
    this.ptAge = new Float32Array(capacity * points);

    this.used = new Uint8Array(capacity);
    this.fading = new Uint8Array(capacity);
    this.start = new Int32Array(capacity);
    this.count = new Int32Array(capacity);
    this.dirty = new Uint8Array(capacity);
    this.collapsed = new Uint8Array(capacity);
    this.collapsed.fill(1);
    this.last = new Float32Array(capacity * 3);
    this.styleWidth = new Float32Array(capacity);
    this.styleAge = new Float32Array(capacity);
    this.styleMinDist = new Float32Array(capacity);
    this.styleTail = new Float32Array(capacity);
    this.styleAlpha = new Float32Array(capacity);
    this.styleMinAngular = new Float32Array(capacity);
    this.styleBody = new Float32Array(capacity * 3);
    this.styleCore = new Float32Array(capacity * 3);
  }

  alloc(style: RibbonStyle): number {
    for (let i = 0; i < this.capacity; i++) {
      if (this.used[i] === 0) {
        this.used[i] = 1;
        this.fading[i] = 0;
        this.start[i] = 0;
        this.count[i] = 0;
        this.setStyle(i, style);
        this.markDirty(i);
        return i;
      }
    }
    return -1;
  }

  setStyle(h: number, s: RibbonStyle): void {
    if (h < 0 || this.used[h] === 0) return;
    this.styleWidth[h] = s.width;
    this.styleAge[h] = s.maxAge;
    this.styleMinDist[h] = s.minDist;
    this.styleTail[h] = s.tailScale;
    this.styleAlpha[h] = s.alpha;
    this.styleMinAngular[h] = s.minAngular;
    _c.setHex(s.body, THREE.SRGBColorSpace);
    this.styleBody[h * 3] = _c.r;
    this.styleBody[h * 3 + 1] = _c.g;
    this.styleBody[h * 3 + 2] = _c.b;
    _c.setHex(s.core, THREE.SRGBColorSpace);
    this.styleCore[h * 3] = _c.r;
    this.styleCore[h * 3 + 1] = _c.g;
    this.styleCore[h * 3 + 2] = _c.b;
  }

  /** Stop feeding this ribbon; it ages out and frees itself. */
  release(h: number): void {
    if (h < 0 || this.used[h] === 0) return;
    this.fading[h] = 1;
  }

  /** Kill immediately, collapsing its geometry this frame. */
  kill(h: number): void {
    if (h < 0 || this.used[h] === 0) return;
    this.used[h] = 0;
    this.fading[h] = 0;
    this.count[h] = 0;
    this.collapse(h);
  }

  isLive(h: number): boolean {
    return h >= 0 && h < this.capacity && this.used[h] === 1;
  }

  /** Append a point, respecting the ribbon's minimum spacing. */
  feed(h: number, x: number, y: number, z: number): void {
    if (h < 0 || this.used[h] === 0 || this.fading[h] === 1) return;
    const n = this.count[h];
    if (n > 0) {
      const lx = this.last[h * 3];
      const ly = this.last[h * 3 + 1];
      const lz = this.last[h * 3 + 2];
      const dx = x - lx;
      const dy = y - ly;
      const dz = z - lz;
      const md = this.styleMinDist[h];
      if (dx * dx + dy * dy + dz * dz < md * md) {
        // Below the spacing threshold: slide the head instead of adding a point, so a
        // slow-moving source still gets a trail that tracks it exactly.
        const head = (this.start[h] + n - 1) % this.points;
        const o = (h * this.points + head) * 3;
        this.ptPos[o] = x;
        this.ptPos[o + 1] = y;
        this.ptPos[o + 2] = z;
        this.markDirty(h);
        return;
      }
    }

    let idx: number;
    if (n < this.points) {
      idx = (this.start[h] + n) % this.points;
      this.count[h] = n + 1;
    } else {
      idx = this.start[h];
      this.start[h] = (this.start[h] + 1) % this.points;
    }
    const o = (h * this.points + idx) * 3;
    this.ptPos[o] = x;
    this.ptPos[o + 1] = y;
    this.ptPos[o + 2] = z;
    this.ptAge[h * this.points + idx] = 0;
    this.last[h * 3] = x;
    this.last[h * 3 + 1] = y;
    this.last[h * 3 + 2] = z;
    this.markDirty(h);
  }

  /** Jump the ribbon to a fresh position with no history (used on throw / dash start). */
  restart(h: number, x: number, y: number, z: number): void {
    if (h < 0 || this.used[h] === 0) return;
    this.start[h] = 0;
    this.count[h] = 0;
    this.fading[h] = 0;
    this.feed(h, x, y, z);
  }

  private markDirty(h: number): void {
    this.dirty[h] = 1;
  }

  private markRange(h: number): void {
    const lo = h * this.points * 2;
    const hi = lo + this.points * 2 - 1;
    if (lo < this.dirtyLo) this.dirtyLo = lo;
    if (hi > this.dirtyHi) this.dirtyHi = hi;
  }

  private collapse(h: number): void {
    if (this.collapsed[h] === 1) return;
    this.collapsed[h] = 1;
    const base = h * this.points * 2;
    for (let i = 0; i < this.points * 2; i++) {
      const v = base + i;
      this.pos[v * 3] = 0;
      this.pos[v * 3 + 1] = -1e5;
      this.pos[v * 3 + 2] = 0;
      this.tan[v * 3] = 0;
      this.tan[v * 3 + 1] = 1;
      this.tan[v * 3 + 2] = 0;
      this.wid[v * 3] = 0;
      this.wid[v * 3 + 1] = 0;
      this.wid[v * 3 + 2] = 0;
    }
    this.markRange(h);
  }

  update(dtSec: number): void {
    for (let h = 0; h < this.capacity; h++) {
      if (this.used[h] === 0) continue;

      // --- age points and retire the tail
      const maxAge = this.styleAge[h];
      const pbase = h * this.points;
      let n = this.count[h];
      let s = this.start[h];
      for (let i = 0; i < n; i++) {
        const idx = (s + i) % this.points;
        this.ptAge[pbase + idx] += dtSec;
      }
      while (n > 0) {
        const idx = s % this.points;
        if (this.ptAge[pbase + idx] < maxAge) break;
        s = (s + 1) % this.points;
        n--;
      }
      this.start[h] = s;
      this.count[h] = n;

      if (n === 0) {
        if (this.fading[h] === 1) {
          this.used[h] = 0;
          this.fading[h] = 0;
        }
        this.collapse(h);
        continue;
      }

      // Ages changed, so a live ribbon's alpha ramp is rewritten every frame.
      this.dirty[h] = 0;
      this.collapsed[h] = 0;
      this.writeRibbon(h, n, s);
      this.markRange(h);
    }

    if (this.dirtyHi >= this.dirtyLo) {
      const lo = this.dirtyLo;
      const count = this.dirtyHi - lo + 1;
      for (let a = 0; a < this.attrs.length; a++) {
        const attr = this.attrs[a];
        attr.clearUpdateRanges();
        attr.addUpdateRange(lo * attr.itemSize, count * attr.itemSize);
        attr.needsUpdate = true;
      }
      this.dirtyLo = Number.MAX_SAFE_INTEGER;
      this.dirtyHi = -1;
    }
  }

  private writeRibbon(h: number, n: number, s: number): void {
    const P = this.points;
    const pbase = h * P;
    const vbase = h * P * 2;
    const width = this.styleWidth[h];
    const tail = this.styleTail[h];
    const alpha = this.styleAlpha[h];
    const minAng = this.styleMinAngular[h];
    const maxAge = this.styleAge[h];
    const br = this.styleBody[h * 3];
    const bg = this.styleBody[h * 3 + 1];
    const bb = this.styleBody[h * 3 + 2];
    const cr = this.styleCore[h * 3];
    const cg = this.styleCore[h * 3 + 1];
    const cb = this.styleCore[h * 3 + 2];

    // Last written vertex pair, reused to collapse the unused tail of the block.
    let lx = 0;
    let ly = 0;
    let lz = 0;

    for (let i = 0; i < P; i++) {
      const live = i < n;
      const idx = ((s + Math.min(i, n - 1)) % P) * 3;
      const o = pbase * 3 + idx;
      const x = this.ptPos[o];
      const y = this.ptPos[o + 1];
      const z = this.ptPos[o + 2];

      // Tangent by central difference over the neighbouring points.
      const ia = ((s + Math.max(0, Math.min(i, n - 1) - 1)) % P) * 3;
      const ib = ((s + Math.min(n - 1, Math.min(i, n - 1) + 1)) % P) * 3;
      let tx = this.ptPos[pbase * 3 + ib] - this.ptPos[pbase * 3 + ia];
      let ty = this.ptPos[pbase * 3 + ib + 1] - this.ptPos[pbase * 3 + ia + 1];
      let tz = this.ptPos[pbase * 3 + ib + 2] - this.ptPos[pbase * 3 + ia + 2];
      const tl = Math.hypot(tx, ty, tz);
      if (tl > 1e-5) {
        tx /= tl;
        ty /= tl;
        tz /= tl;
      } else {
        tx = 0;
        ty = 1;
        tz = 0;
      }

      let wdt = 0;
      let alp = 0;
      if (live) {
        const t = n > 1 ? i / (n - 1) : 1;
        // Head is fattest; the tail thins to `tailScale`, which is what makes a trail
        // read as motion rather than as a static tube.
        wdt = width * (tail + (1 - tail) * t);
        const age = this.ptAge[pbase + ((s + i) % P)];
        const ageT = clamp01(1 - age / Math.max(maxAge, 1e-3));
        alp = alpha * ageT * ageT;
        lx = x;
        ly = y;
        lz = z;
      }

      const v0 = (vbase + i * 2) * 3;
      const v1 = v0 + 3;
      const px = live ? x : lx;
      const py = live ? y : ly;
      const pz = live ? z : lz;
      this.pos[v0] = px;
      this.pos[v0 + 1] = py;
      this.pos[v0 + 2] = pz;
      this.pos[v1] = px;
      this.pos[v1 + 1] = py;
      this.pos[v1 + 2] = pz;
      this.tan[v0] = tx;
      this.tan[v0 + 1] = ty;
      this.tan[v0 + 2] = tz;
      this.tan[v1] = tx;
      this.tan[v1 + 1] = ty;
      this.tan[v1 + 2] = tz;
      this.col[v0] = br;
      this.col[v0 + 1] = bg;
      this.col[v0 + 2] = bb;
      this.col[v1] = br;
      this.col[v1 + 1] = bg;
      this.col[v1 + 2] = bb;
      this.core[v0] = cr;
      this.core[v0 + 1] = cg;
      this.core[v0 + 2] = cb;
      this.core[v1] = cr;
      this.core[v1 + 1] = cg;
      this.core[v1 + 2] = cb;
      // A dead point still carries the ribbon's angular floor, so the collapsed tail
      // stays a zero-area degenerate rather than being inflated back into a visible
      // quad by the minimum-width term.
      const w0 = (vbase + i * 2) * 3;
      this.wid[w0] = wdt;
      this.wid[w0 + 1] = alp;
      this.wid[w0 + 2] = live ? minAng : 0;
      this.wid[w0 + 3] = wdt;
      this.wid[w0 + 4] = alp;
      this.wid[w0 + 5] = live ? minAng : 0;
    }
  }

  clear(): void {
    for (let h = 0; h < this.capacity; h++) {
      if (this.used[h] === 1) this.kill(h);
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Grapple rope
// ---------------------------------------------------------------------------

const ROPE_VERT = /* glsl */ `
attribute float aT;

varying vec3 vNormal;
varying vec3 vView;
varying float vT;

void main() {

	vec4 mv = modelViewMatrix * vec4( position, 1.0 );
	vNormal = normalize( normalMatrix * normal );
	vView = normalize( - mv.xyz );
	vT = aT;
	gl_Position = projectionMatrix * mv;

}
`;

const ROPE_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform vec3 uHot;
uniform vec3 uInk;
uniform float uPulse;
uniform float uTaut;

varying vec3 vNormal;
varying vec3 vView;
varying float vT;

void main() {

	vec3 n = normalize( vNormal );

	// Two hard bands from a fixed key direction. A rope lit with a smooth falloff would
	// be the only smoothly-shaded object in the frame, so it gets the same stepped
	// treatment as everything else.
	vec3 key = normalize( vec3( 0.42, 0.78, 0.46 ) );
	float ndl = dot( n, key );
	float lit = ndl > 0.12 ? 1.0 : 0.42;
	lit = ndl > 0.66 ? 1.25 : lit;

	// Ink the silhouette so the rope keeps a drawn outline against any background.
	float rim = 1.0 - clamp( dot( n, normalize( vView ) ), 0.0, 1.0 );
	float edge = smoothstep( 0.72, 0.92, rim );

	// A tension pulse runs from the hand to the anchor; it goes brighter and faster the
	// tauter the tether is, which is the whole read on when to release.
	//
	// Two bands, not one: a hard white head with a longer trailing smear behind it. One
	// exponential spike on a rope only a few pixels wide lands on a single ring of the
	// tube and is gone before the eye finds it, which is how a tension cue ends up
	// invisible on the exact frame the player is meant to be timing a release from.
	float pd = abs( fract( vT - uPulse + 0.5 ) - 0.5 );
	float pulse = exp( - 22.0 * pd ) + 0.45 * exp( - 7.0 * pd );
	pulse = clamp( pulse, 0.0, 1.0 );

	// A taut tether is BRIGHTER along its whole length, not just where the pulse is:
	// tension has to be readable in peripheral vision, and a travelling highlight is not.
	vec3 body = mix( uColor * lit, uColor * lit * 1.35 + vec3( 0.10 ), uTaut );
	vec3 col = mix( body, uHot, clamp( pulse * ( 0.40 + 0.60 * uTaut ), 0.0, 1.0 ) );

	// Ink the silhouette last so the rope keeps a drawn outline over sky and plaster
	// alike. Opaque throughout — a translucent tether over a sunlit white atrium is a
	// tether nobody can see, and this one is load-bearing feedback, not atmosphere.
	col = mix( col, uInk, edge );

	gl_FragColor = vec4( col, 1.0 );

}
`;

class GrappleRope {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly pos: Float32Array;
  private readonly nrm: Float32Array;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly nrmAttr: THREE.BufferAttribute;
  /** Scratch centreline, allocated once. */
  private readonly line = new Float32Array(ROPE_RINGS * 3);

  constructor() {
    const verts = ROPE_RINGS * ROPE_SIDES;
    this.pos = new Float32Array(verts * 3);
    this.nrm = new Float32Array(verts * 3);
    const t = new Float32Array(verts);
    for (let r = 0; r < ROPE_RINGS; r++) {
      const tv = r / (ROPE_RINGS - 1);
      for (let s = 0; s < ROPE_SIDES; s++) t[r * ROPE_SIDES + s] = tv;
    }

    const index = new Uint16Array((ROPE_RINGS - 1) * ROPE_SIDES * 6);
    let w = 0;
    for (let r = 0; r < ROPE_RINGS - 1; r++) {
      for (let s = 0; s < ROPE_SIDES; s++) {
        const s1 = (s + 1) % ROPE_SIDES;
        const a = r * ROPE_SIDES + s;
        const b = r * ROPE_SIDES + s1;
        const c = (r + 1) * ROPE_SIDES + s1;
        const d = (r + 1) * ROPE_SIDES + s;
        index[w++] = a;
        index[w++] = b;
        index[w++] = c;
        index[w++] = a;
        index[w++] = c;
        index[w++] = d;
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.name = 'vfx-grapple-rope';
    this.posAttr = new THREE.BufferAttribute(this.pos, 3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.nrmAttr = new THREE.BufferAttribute(this.nrm, 3);
    this.nrmAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', this.posAttr);
    geo.setAttribute('normal', this.nrmAttr);
    geo.setAttribute('aT', new THREE.BufferAttribute(t, 1));
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    const mat = new THREE.ShaderMaterial({
      name: 'vfx-grapple-rope',
      uniforms: {
        uColor: { value: new THREE.Color().setHex(VFX.ink, THREE.SRGBColorSpace) },
        // The tension pulse is white, not amber: amber is the chamber, and a tether that
        // flashed amber under load would read as a loaded gun crossing the arena.
        uHot: { value: new THREE.Color().setHex(VFX.core, THREE.SRGBColorSpace) },
        uInk: { value: new THREE.Color().setHex(VFX.ink, THREE.SRGBColorSpace) },
        uPulse: { value: 0 },
        uTaut: { value: 0 },
      },
      vertexShader: ROPE_VERT,
      fragmentShader: ROPE_FRAG,
      side: THREE.FrontSide,
      toneMapped: false,
    });

    this.geometry = geo;
    this.material = mat;
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.name = 'vfx-grapple-rope';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.visible = false;
    this.mesh.renderOrder = 6;
  }

  setTeam(team: number): void {
    (this.material.uniforms.uColor.value as THREE.Color).setHex(
      TEAM_COLORS[team === 1 ? 1 : 0],
      THREE.SRGBColorSpace,
    );
  }

  hide(): void {
    this.mesh.visible = false;
  }

  /**
   * Sweep the rope between two points.
   *
   * `restLength` is the tether's rest length: shorter than the gap means taut (straight,
   * vibrating, pulsing hard), longer means slack (it hangs).
   *
   * `vx/vy/vz` is the viewer's eye. It is used for one thing only: enforcing
   * {@link ROPE_MIN_ANGULAR} per ring, so the far end of a sixty-metre tether is still a
   * visible line instead of a sub-pixel thread.
   */
  update(
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    restLength: number,
    time: number,
    vx: number,
    vy: number,
    vz: number,
  ): void {
    this.mesh.visible = true;

    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-4) {
      this.mesh.visible = false;
      return;
    }

    const slack = Math.max(0, restLength - dist);
    const sag = Math.min(ROPE_MAX_SAG, slack * 0.55 + dist * 0.035);
    const taut = clamp01((dist - restLength * 0.94) / Math.max(0.4, restLength * 0.08));
    this.material.uniforms.uTaut.value = taut;
    // Pulse runs hand -> anchor, faster when taut.
    this.material.uniforms.uPulse.value = (time * (0.55 + taut * 1.5)) % 1;

    // Axis frame for the rope cross section.
    const ux = dx / dist;
    const uy = dy / dist;
    const uz = dz / dist;
    let hx = 0;
    let hy = 1;
    let hz = 0;
    if (Math.abs(uy) > 0.94) {
      hx = 1;
      hy = 0;
      hz = 0;
    }
    let rx = uy * hz - uz * hy;
    let ry = uz * hx - ux * hz;
    let rz = ux * hy - uy * hx;
    const rl = Math.hypot(rx, ry, rz) || 1;
    rx /= rl;
    ry /= rl;
    rz /= rl;

    // --- centreline: straight lerp + parabolic sag + a taut vibration
    const vib = ROPE_VIBRATION * taut;
    for (let i = 0; i < ROPE_RINGS; i++) {
      const t = i / (ROPE_RINGS - 1);
      const drop = 4 * t * (1 - t);
      const wob = Math.sin(t * 17.0 + time * 34.0) * vib * Math.sin(Math.PI * t);
      this.line[i * 3] = ax + dx * t + rx * wob;
      this.line[i * 3 + 1] = ay + dy * t - sag * drop + ry * wob;
      this.line[i * 3 + 2] = az + dz * t + rz * wob;
    }

    // --- sweep the cross section
    for (let i = 0; i < ROPE_RINGS; i++) {
      const cx = this.line[i * 3];
      const cy = this.line[i * 3 + 1];
      const cz = this.line[i * 3 + 2];

      // Local tangent, so the tube stays round through the sag.
      const ia = Math.max(0, i - 1) * 3;
      const ib = Math.min(ROPE_RINGS - 1, i + 1) * 3;
      let tx = this.line[ib] - this.line[ia];
      let ty = this.line[ib + 1] - this.line[ia + 1];
      let tz = this.line[ib + 2] - this.line[ia + 2];
      const tl = Math.hypot(tx, ty, tz) || 1;
      tx /= tl;
      ty /= tl;
      tz /= tl;

      // Re-orthogonalise the fixed frame against the local tangent.
      const dr = rx * tx + ry * ty + rz * tz;
      let n0x = rx - tx * dr;
      let n0y = ry - ty * dr;
      let n0z = rz - tz * dr;
      const n0l = Math.hypot(n0x, n0y, n0z) || 1;
      n0x /= n0l;
      n0y /= n0l;
      n0z /= n0l;
      const n1x = ty * n0z - tz * n0y;
      const n1y = tz * n0x - tx * n0z;
      const n1z = tx * n0y - ty * n0x;

      // Taper BOTH ends into their attachments. The anchor end always did; the hand end
      // now does too, because the first ring of a rope this thick sits well under a metre
      // from the lens and would otherwise be a slab across the corner of the frame.
      const tt = i / (ROPE_RINGS - 1);
      const near = (tt < 1 - tt ? tt : 1 - tt) / ROPE_TAPER_SPAN;
      const taper = ROPE_TAPER_MIN + (1 - ROPE_TAPER_MIN) * (near < 1 ? near : 1);

      // Distance may thin the tether; it may never erase it. Same guarantee the ribbon
      // pool gives every trail, done on the CPU here because the rope is a real swept
      // tube rather than a view-facing ribbon.
      const vdx = cx - vx;
      const vdy = cy - vy;
      const vdz = cz - vz;
      const vd = Math.sqrt(vdx * vdx + vdy * vdy + vdz * vdz);
      const floorR = vd * ROPE_MIN_ANGULAR;
      const radius = (floorR > ROPE_RADIUS ? floorR : ROPE_RADIUS) * taper;

      for (let s = 0; s < ROPE_SIDES; s++) {
        const a = (s / ROPE_SIDES) * Math.PI * 2;
        const ca = Math.cos(a);
        const sa = Math.sin(a);
        const nx = n0x * ca + n1x * sa;
        const ny = n0y * ca + n1y * sa;
        const nz = n0z * ca + n1z * sa;
        const o = (i * ROPE_SIDES + s) * 3;
        this.pos[o] = cx + nx * radius;
        this.pos[o + 1] = cy + ny * radius;
        this.pos[o + 2] = cz + nz * radius;
        this.nrm[o] = nx;
        this.nrm[o + 1] = ny;
        this.nrm[o + 2] = nz;
      }
    }

    this.posAttr.needsUpdate = true;
    this.nrmAttr.needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Per-entity bookkeeping
// ---------------------------------------------------------------------------

interface GunTrailRec {
  core: number;
  halo: number;
  sparkAccum: number;
  pipAccum: number;
  chambered: boolean;
  seen: boolean;
}

interface PlayerTrailRec {
  bleed: number;
  dash: number;
  dashUntil: number;
  scrape: number;
  scrapeAccum: number;
  dripAccum: number;
  rope: number;
  wasGrappling: boolean;
  seen: boolean;
}

function newGunRec(): GunTrailRec {
  return { core: -1, halo: -1, sparkAccum: 0, pipAccum: 0, chambered: false, seen: false };
}

function newPlayerRec(): PlayerTrailRec {
  return {
    bleed: -1,
    dash: -1,
    dashUntil: 0,
    scrape: -1,
    scrapeAccum: 0,
    dripAccum: 0,
    rope: -1,
    wasGrappling: false,
    seen: false,
  };
}

// ---------------------------------------------------------------------------
// TrailSystem
// ---------------------------------------------------------------------------

const _dir: Vec3 = v3();
const _right: Vec3 = v3();
const _hand: Vec3 = v3();

export class TrailSystem {
  private readonly scene: THREE.Scene;
  private readonly particles: ParticleSystem;
  private readonly glow: RibbonPool;
  private readonly ink: RibbonPool;
  private readonly ropes: GrappleRope[] = [];
  private readonly ropeOwner: (string | null)[] = [];

  private readonly guns = new Map<number, GunTrailRec>();
  private readonly players = new Map<string, PlayerTrailRec>();
  private readonly gunPool: GunTrailRec[] = [];
  private readonly playerPool: PlayerTrailRec[] = [];

  private time = 0;
  private disposed = false;

  /** The local player's eye, refreshed every frame. Only the tether's width floor uses it. */
  private readonly viewer: Vec3 = v3();
  private hasViewer = false;

  constructor(scene: THREE.Scene, particles: ParticleSystem, renderOrder = 10) {
    this.scene = scene;
    this.particles = particles;
    this.ink = new RibbonPool(RIBBON_CAPACITY, RIBBON_POINTS, false, renderOrder - 1);
    this.glow = new RibbonPool(RIBBON_CAPACITY, RIBBON_POINTS, true, renderOrder);
    scene.add(this.ink.mesh);
    scene.add(this.glow.mesh);

    for (let i = 0; i < ROPE_POOL; i++) {
      const r = new GrappleRope();
      this.ropes.push(r);
      this.ropeOwner.push(null);
      scene.add(r.mesh);
    }
  }

  // -------------------------------------------------------------------------
  // One-shot hooks (driven by GameEvents through VfxSystem)
  // -------------------------------------------------------------------------

  /**
   * A dash leaves a wide, short-lived wake in team colour. Skipped for the local view.
   *
   * Matte and translucent: an additive wake washed out entirely against the arena, but a
   * fully opaque one would be a player-sized shape someone could hide behind, which the
   * readability contract forbids outright.
   */
  dash(p: PlayerState, isLocal: boolean): void {
    if (isLocal) return;
    const rec = this.playerRec(p.id);
    if (rec.dash < 0) {
      _style.body = TEAM_COLORS[p.team === 1 ? 1 : 0];
      // Ink down the middle, not white. A dash wake is matte; a white spine in a matte
      // ribbon is a bright stripe on a bright world, i.e. a gap in the shape.
      _style.core = VFX.ink;
      _style.width = DASH_TRAIL_WIDTH;
      _style.maxAge = DASH_TRAIL_AGE;
      _style.minDist = 0.22;
      _style.tailScale = 0.05;
      _style.alpha = DASH_TRAIL_ALPHA;
      _style.minAngular = MIN_ANG_DASH;
      rec.dash = this.ink.alloc(_style);
    }
    if (rec.dash >= 0) {
      this.ink.restart(rec.dash, p.pos.x, p.pos.y + PLAYER_HEIGHT * 0.55, p.pos.z);
      rec.dashUntil = this.time + 0.34;
    }
  }

  /** Fresh arc for a gun that just left someone's hands. */
  gunThrown(g: GunEntity): void {
    const rec = this.gunRec(g.id);
    this.styleGunTrail(rec, g);
    // Primed, so a loaded pass drops its first pip on the release point rather than a
    // frame or two down the arc. The beat should start where the throw does.
    rec.pipAccum = g.chambered ? 1 : 0;
    if (rec.core >= 0) this.ink.restart(rec.core, g.pos.x, g.pos.y, g.pos.z);
    if (rec.halo >= 0) this.glow.restart(rec.halo, g.pos.x, g.pos.y, g.pos.z);
  }

  /** The gun is no longer in flight — let its arc hang in the air and fade. */
  gunLanded(gunId: number): void {
    const rec = this.guns.get(gunId);
    if (rec === undefined) return;
    if (rec.core >= 0) this.ink.release(rec.core);
    if (rec.halo >= 0) this.glow.release(rec.halo);
    rec.core = -1;
    rec.halo = -1;
  }

  // -------------------------------------------------------------------------
  // Per-frame, driven by world state
  // -------------------------------------------------------------------------

  update(dtSec: number, w: WorldState, localId: string): void {
    if (this.disposed) return;
    const dt = dtSec > 0.25 ? 0.25 : dtSec > 0 ? dtSec : 0;
    this.time += dt;

    const me = w.players[localId];
    if (me !== undefined) {
      this.viewer.x = me.pos.x;
      this.viewer.y = me.pos.y + (me.crouching ? PLAYER_EYE_HEIGHT * 0.55 : PLAYER_EYE_HEIGHT);
      this.viewer.z = me.pos.z;
      this.hasViewer = true;
    }

    for (const rec of this.guns.values()) rec.seen = false;
    for (const rec of this.players.values()) rec.seen = false;

    this.updateGuns(dt, w);
    this.updatePlayers(dt, w, localId);
    this.reapUnseen();

    this.ink.update(dt);
    this.glow.update(dt);
  }

  private updateGuns(dt: number, w: WorldState): void {
    for (let i = 0; i < w.guns.length; i++) {
      const g = w.guns[i];
      if (g.state !== GunState.InFlight) {
        const existing = this.guns.get(g.id);
        if (existing !== undefined) {
          existing.seen = true;
          if (existing.core >= 0 || existing.halo >= 0) this.gunLanded(g.id);
        }
        continue;
      }

      const rec = this.gunRec(g.id);
      rec.seen = true;
      if (rec.core < 0 || rec.chambered !== g.chambered) this.styleGunTrail(rec, g);

      if (rec.core >= 0) this.ink.feed(rec.core, g.pos.x, g.pos.y, g.pos.z);
      if (rec.halo >= 0) this.glow.feed(rec.halo, g.pos.x, g.pos.y, g.pos.z);

      if (g.chambered) {
        this.markChamberedBeat(rec, g, dt);
        this.shedChamberedEmbers(rec, g, dt);
      } else {
        rec.pipAccum = 0;
      }
    }
  }

  /**
   * The beaded half of a loaded pass: one hollow matte amber ring pinned in the air per
   * beat, so the arc reads as a DOTTED line rather than a coloured one. See
   * {@link CHAMBER_PIP_HZ} — this is the channel that survives greyscale and distance.
   */
  private markChamberedBeat(rec: GunTrailRec, g: GunEntity, dt: number): void {
    rec.pipAccum += dt * CHAMBER_PIP_HZ;
    const pips = rec.pipAccum | 0;
    if (pips <= 0) return;
    rec.pipAccum -= pips;

    const ps = this.particles;
    const d = ps.begin(P_RING, false);
    // Never more than one per frame: at 13 Hz a single dropped frame is the only way this
    // asks for two, and two pips at the same point is a blob rather than a beat.
    d.count = 1;
    d.px = g.pos.x;
    d.py = g.pos.y;
    d.pz = g.pos.z;
    // Left standing where the gun was. No velocity, no gravity, no drift.
    d.life = CHAMBER_PIP_LIFE;
    d.lifeVar = 0;
    d.size0 = CHAMBER_PIP_SIZE0;
    d.size1 = CHAMBER_PIP_SIZE1;
    d.sizeVar = 0;
    d.color0 = VFX.hot;
    d.color1 = VFX.hot;
    d.drag = 4;
    // Priority 3: the pass is the single most important thing on screen, and a wall of
    // landing dust must never be able to evict the marks that say it is loaded.
    d.priority = 3;
    ps.emit();
  }

  /**
   * What a chambered gun leaves behind it in flight: matte amber embers that tumble and
   * fall, and a thinner stream of additive sparks riding on top of them.
   *
   * The embers do the work. They are opaque shapes with an ink outline, so they read as
   * a shower of hot debris whether the pass crosses a white wall, a glass curtain or open
   * sky. The sparks only supply the bloom — on their own, against this world, they would
   * be a faint warm haze and nothing more.
   */
  private shedChamberedEmbers(rec: GunTrailRec, g: GunEntity, dt: number): void {
    rec.sparkAccum += dt * (LOADED_SPARK_HZ + LOADED_EMBER_HZ);
    const n = rec.sparkAccum | 0;
    if (n <= 0) return;
    rec.sparkAccum -= n;

    // Split the budget in the ratio the two rates ask for, never dropping the embers.
    const embers = Math.max(1, Math.round((n * LOADED_EMBER_HZ) / (LOADED_SPARK_HZ + LOADED_EMBER_HZ)));
    const sparks = n - embers;

    const ps = this.particles;
    let d = ps.begin(P_CHIP, false);
    d.count = embers;
    d.px = g.pos.x;
    d.py = g.pos.y;
    d.pz = g.pos.z;
    d.posJitter = 0.1;
    d.vx = -g.vel.x * 0.16;
    d.vy = -g.vel.y * 0.16 + 0.5;
    d.vz = -g.vel.z * 0.16;
    d.velJitter = 1.6;
    d.gravity = -7;
    d.drag = 2.4;
    d.life = 0.42;
    d.lifeVar = 0.4;
    d.size0 = 0.11;
    d.size1 = 0.03;
    d.color0 = VFX.hot;
    d.color1 = VFX.danger;
    d.spinRate = 7;
    d.spinVar = 7;
    d.priority = 2;
    ps.emit();

    if (sparks <= 0) return;
    d = ps.begin(P_SPARK, true);
    d.count = sparks;
    d.px = g.pos.x;
    d.py = g.pos.y;
    d.pz = g.pos.z;
    d.posJitter = 0.09;
    d.vx = -g.vel.x * 0.18;
    d.vy = -g.vel.y * 0.18 + 0.4;
    d.vz = -g.vel.z * 0.18;
    d.velJitter = 1.5;
    d.gravity = -6;
    d.drag = 3.2;
    d.life = 0.26;
    d.lifeVar = 0.4;
    d.size0 = 0.16;
    d.size1 = 0.02;
    d.color0 = VFX.core;
    d.color1 = VFX.hot;
    d.intensity0 = 3.2;
    d.intensity1 = 1.0;
    d.bloom = 1;
    d.stretch = 0.8;
    d.priority = 1;
    ps.emit();
  }

  private styleGunTrail(rec: GunTrailRec, g: GunEntity): void {
    rec.chambered = g.chambered;
    const team = TEAM_COLORS[g.team === 1 ? 1 : 0];

    // --- the stroke that carries the read. MATTE, so it is a drawn line on any
    //     background rather than a wash that only exists where the world is dark.
    _style.body = g.chambered ? VFX.hot : team;
    // A chambered arc is SOLID amber edge to edge. It used to run a white stripe down
    // the middle to sell heat, but white on white architecture subtracts the very thing
    // the stroke exists to say — at any distance past a few metres the highlight was
    // most of the line, and a loaded pass read as a pale streak rather than as amber.
    // The incandescence now lives entirely in the additive halo below, which is what
    // additive is actually good for.
    //
    // The empty arc takes the opposite spine: INK. Dark inside team colour is legible by
    // value as well as by hue, so an empty pass survives crossing a white wall and a
    // bright sky, and it still cannot be confused with the loaded one for a moment.
    _style.core = g.chambered ? VFX.hot : VFX.ink;
    _style.width = g.chambered ? GUN_TRAIL_WIDTH_LOADED : GUN_TRAIL_WIDTH_EMPTY;
    _style.maxAge = g.chambered ? GUN_TRAIL_AGE_LOADED : GUN_TRAIL_AGE_EMPTY;
    _style.minDist = GUN_TRAIL_MIN_DIST;
    _style.tailScale = g.chambered ? 0.2 : 0.08;
    _style.alpha = g.chambered ? 1 : GUN_TRAIL_ALPHA_EMPTY;
    _style.minAngular = g.chambered ? MIN_ANG_LOADED : MIN_ANG_EMPTY;
    if (rec.core < 0) rec.core = this.ink.alloc(_style);
    else this.ink.setStyle(rec.core, _style);

    // --- halo. Chambered only, and the ONLY additive part of the arc: a soft amber
    //     bloom hugging the matte stroke. An empty gun must stay visually quiet.
    if (g.chambered) {
      _style.body = VFX.hot;
      // White is allowed here and only here: this pass is additive, it is drawn ON TOP
      // of a matte amber stroke that has already established the shape, and its whole
      // job is to be the part that blooms.
      _style.core = VFX.core;
      _style.width = GUN_HALO_WIDTH;
      _style.maxAge = GUN_HALO_AGE;
      _style.minDist = GUN_TRAIL_MIN_DIST;
      _style.tailScale = 0.02;
      _style.alpha = GUN_HALO_ALPHA;
      _style.minAngular = MIN_ANG_HALO;
      if (rec.halo < 0) rec.halo = this.glow.alloc(_style);
      else this.glow.setStyle(rec.halo, _style);
    } else if (rec.halo >= 0) {
      this.glow.release(rec.halo);
      rec.halo = -1;
    }
  }

  private updatePlayers(dt: number, w: WorldState, localId: string): void {
    for (const id in w.players) {
      const p = w.players[id];
      const rec = this.playerRec(id);
      rec.seen = true;
      const isLocal = id === localId;

      if (p.healthState === HealthState.Dead) {
        this.releasePlayerTrails(rec);
        continue;
      }

      // --- bleed: the wounded leak ink, and that is enemy intel by design.
      if (p.healthState === HealthState.Wounded) {
        if (rec.bleed < 0) {
          _style.body = VFX.dangerDeep;
          _style.core = VFX.danger;
          _style.width = BLEED_WIDTH;
          _style.maxAge = BLEED_AGE;
          _style.minDist = 0.30;
          _style.tailScale = 0.25;
          _style.alpha = 0.85;
          _style.minAngular = MIN_ANG_BLEED;
          rec.bleed = this.ink.alloc(_style);
        }
        const hy = p.pos.y + PLAYER_HEIGHT * (p.crouching ? 0.30 : 0.48);
        if (rec.bleed >= 0) this.ink.feed(rec.bleed, p.pos.x, hy, p.pos.z);

        rec.dripAccum += dt * BLEED_DRIP_HZ;
        const drips = rec.dripAccum | 0;
        if (drips > 0) {
          rec.dripAccum -= drips;
          const d = this.particles.begin(P_INK, false);
          d.count = drips;
          d.px = p.pos.x;
          d.py = hy;
          d.pz = p.pos.z;
          d.posJitter = 0.16;
          d.vx = p.vel.x * 0.25;
          d.vy = -0.6;
          d.vz = p.vel.z * 0.25;
          d.velJitter = 0.5;
          d.gravity = -18;
          d.drag = 0.6;
          d.life = 0.9;
          d.lifeVar = 0.35;
          d.size0 = 0.085;
          d.size1 = 0.06;
          d.color0 = VFX.danger;
          d.color1 = VFX.dangerDeep;
          d.intensity0 = 1;
          d.intensity1 = 1;
          d.spinRate = 3;
          d.spinVar = 3;
          d.priority = 1;
          this.particles.emit();
        }
      } else if (rec.bleed >= 0) {
        this.ink.release(rec.bleed);
        rec.bleed = -1;
        rec.dripAccum = 0;
      }

      // --- wall-run scrape
      if (p.moveState === MoveState.WallRunning && p.wallNormal !== null) {
        const n = p.wallNormal;
        const sx = p.pos.x + n.x * 0.42;
        const sy = p.pos.y + PLAYER_HEIGHT * 0.32;
        const sz = p.pos.z + n.z * 0.42;
        // A scuff mark, not a firework. Wall-running is movement, and movement is not a
        // signal — under the colour contract it gets ink and nothing else, which also
        // means it draws as a genuine dark scrape down a white wall instead of an amber
        // glow that would read, wrongly and at a glance, as a chambered round.
        if (rec.scrape < 0) {
          _style.body = VFX.dust;
          _style.core = VFX.ink;
          _style.width = SCRAPE_WIDTH;
          _style.maxAge = SCRAPE_AGE;
          _style.minDist = 0.22;
          _style.tailScale = 0.08;
          _style.alpha = 0.7;
          _style.minAngular = MIN_ANG_SCRAPE;
          rec.scrape = this.ink.alloc(_style);
          if (rec.scrape >= 0) this.ink.restart(rec.scrape, sx, sy, sz);
        }
        if (rec.scrape >= 0) this.ink.feed(rec.scrape, sx, sy, sz);

        rec.scrapeAccum += dt * SCRAPE_GRIT_HZ;
        const grit = rec.scrapeAccum | 0;
        if (grit > 0) {
          rec.scrapeAccum -= grit;
          const d = this.particles.begin(P_CHIP, false);
          d.count = grit;
          d.px = sx;
          d.py = sy;
          d.pz = sz;
          d.posJitter = 0.14;
          d.vx = -p.vel.x * 0.3 + n.x * 2.2;
          d.vy = -p.vel.y * 0.2 - 0.5;
          d.vz = -p.vel.z * 0.3 + n.z * 2.2;
          d.spread = 0.7;
          d.speedVar = 0.6;
          d.velJitter = 1.6;
          d.gravity = -16;
          d.drag = 2.2;
          d.life = 0.4;
          d.lifeVar = 0.5;
          d.size0 = 0.07;
          d.size1 = 0.03;
          d.color0 = VFX.dust;
          d.color1 = VFX.ink;
          d.spinRate = 8;
          d.spinVar = 8;
          d.priority = 1;
          this.particles.emit();
        }
      } else if (rec.scrape >= 0) {
        this.ink.release(rec.scrape);
        rec.scrape = -1;
        rec.scrapeAccum = 0;
      }

      // --- dash wake
      if (rec.dash >= 0) {
        if (this.time > rec.dashUntil || p.moveState !== MoveState.Dashing) {
          this.ink.release(rec.dash);
          rec.dash = -1;
        } else {
          this.ink.feed(rec.dash, p.pos.x, p.pos.y + PLAYER_HEIGHT * 0.55, p.pos.z);
        }
      }

      // --- grapple tether
      const grappling = p.grappleAnchorId !== GRAPPLE_NONE && p.grapplePoint !== null;
      if (grappling) {
        if (rec.rope < 0) rec.rope = this.claimRope(id);
        if (rec.rope >= 0) {
          const rope = this.ropes[rec.rope];
          rope.setTeam(p.team);
          this.handPosition(p, isLocal, _hand);
          const target = p.grapplePoint as Vec3;
          const prog = clamp01(p.grappleHookProgress);
          // While the hook is still travelling the rope only reaches as far as the hook.
          const tx = _hand.x + (target.x - _hand.x) * prog;
          const ty = _hand.y + (target.y - _hand.y) * prog;
          const tz = _hand.z + (target.z - _hand.z) * prog;
          const rest =
            prog < 1
              ? Math.max(GRAPPLE_MIN_LENGTH, Math.hypot(tx - _hand.x, ty - _hand.y, tz - _hand.z))
              : Math.max(GRAPPLE_MIN_LENGTH, p.grappleLength);
          // No local player in the snapshot (spectating, or the first frame after a
          // join) means no eye to measure against, so the width floor falls back to the
          // hand — which for a first-person view is very nearly the same point anyway.
          const vx = this.hasViewer ? this.viewer.x : _hand.x;
          const vy = this.hasViewer ? this.viewer.y : _hand.y;
          const vz = this.hasViewer ? this.viewer.z : _hand.z;
          rope.update(_hand.x, _hand.y, _hand.z, tx, ty, tz, rest, this.time, vx, vy, vz);
        }
        rec.wasGrappling = true;
      } else if (rec.rope >= 0) {
        this.ropes[rec.rope].hide();
        this.ropeOwner[rec.rope] = null;
        rec.rope = -1;
        rec.wasGrappling = false;
      }
    }
  }

  /**
   * Where the tether leaves the body. For a remote player that is the shoulder; for the
   * local player it is pushed forward, down and to the side of the eye so the rope never
   * crosses the crosshair.
   */
  private handPosition(p: PlayerState, isLocal: boolean, out: Vec3): void {
    dirFromYawPitch(_dir, p.yaw, p.pitch);
    rightFromYaw(_right, p.yaw);
    const eyeY = p.pos.y + (p.crouching ? PLAYER_EYE_HEIGHT * 0.55 : PLAYER_EYE_HEIGHT);
    const fwd = isLocal ? 0.55 : 0.25;
    const side = isLocal ? 0.34 : 0.28;
    const drop = isLocal ? 0.30 : 0.18;
    out.x = p.pos.x + _dir.x * fwd + _right.x * side;
    out.y = eyeY + _dir.y * fwd - drop;
    out.z = p.pos.z + _dir.z * fwd + _right.z * side;
  }

  private claimRope(owner: string): number {
    for (let i = 0; i < this.ropes.length; i++) {
      if (this.ropeOwner[i] === null) {
        this.ropeOwner[i] = owner;
        return i;
      }
    }
    return -1;
  }

  private gunRec(id: number): GunTrailRec {
    let rec = this.guns.get(id);
    if (rec === undefined) {
      rec = this.gunPool.pop() ?? newGunRec();
      rec.core = -1;
      rec.halo = -1;
      rec.sparkAccum = 0;
      rec.pipAccum = 0;
      rec.chambered = false;
      rec.seen = true;
      this.guns.set(id, rec);
    }
    return rec;
  }

  private playerRec(id: string): PlayerTrailRec {
    let rec = this.players.get(id);
    if (rec === undefined) {
      rec = this.playerPool.pop() ?? newPlayerRec();
      rec.bleed = -1;
      rec.dash = -1;
      rec.dashUntil = 0;
      rec.scrape = -1;
      rec.scrapeAccum = 0;
      rec.dripAccum = 0;
      rec.rope = -1;
      rec.wasGrappling = false;
      rec.seen = true;
      this.players.set(id, rec);
    }
    return rec;
  }

  private releasePlayerTrails(rec: PlayerTrailRec): void {
    if (rec.bleed >= 0) {
      this.ink.release(rec.bleed);
      rec.bleed = -1;
    }
    if (rec.dash >= 0) {
      this.ink.release(rec.dash);
      rec.dash = -1;
    }
    if (rec.scrape >= 0) {
      this.ink.release(rec.scrape);
      rec.scrape = -1;
    }
    if (rec.rope >= 0) {
      this.ropes[rec.rope].hide();
      this.ropeOwner[rec.rope] = null;
      rec.rope = -1;
    }
  }

  /** Free bookkeeping for entities that vanished from the snapshot. */
  private reapUnseen(): void {
    for (const [id, rec] of this.guns) {
      if (rec.seen) continue;
      if (rec.core >= 0) this.ink.release(rec.core);
      if (rec.halo >= 0) this.glow.release(rec.halo);
      this.guns.delete(id);
      this.gunPool.push(rec);
    }
    for (const [id, rec] of this.players) {
      if (rec.seen) continue;
      this.releasePlayerTrails(rec);
      this.players.delete(id);
      this.playerPool.push(rec);
    }
  }

  /** Drop every trail instantly. Called on round transitions. */
  reset(): void {
    for (const rec of this.guns.values()) this.gunPool.push(rec);
    this.guns.clear();
    for (const rec of this.players.values()) this.playerPool.push(rec);
    this.players.clear();
    this.ink.clear();
    this.glow.clear();
    for (let i = 0; i < this.ropes.length; i++) {
      this.ropes[i].hide();
      this.ropeOwner[i] = null;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scene.remove(this.ink.mesh);
    this.scene.remove(this.glow.mesh);
    this.ink.dispose();
    this.glow.dispose();
    for (const r of this.ropes) {
      this.scene.remove(r.mesh);
      r.dispose();
    }
    this.ropes.length = 0;
    this.ropeOwner.length = 0;
    this.guns.clear();
    this.players.clear();
    this.gunPool.length = 0;
    this.playerPool.length = 0;
  }
}
